/**
 * Type Definitions for Matters Service
 *
 * This module defines all TypeScript interfaces and types used by the
 * matters service, including API requests/responses and domain models.
 */

import { z } from 'zod';
import { MatterTypeSlug } from '@/lib/services/matter-types/types';
import type { PmsProvider } from '@/lib/services/integrations/pms';
import type { OutlookSendIdentity } from '@/lib/services/communications/types';
import { INSTRUCTION_TYPE_VALUES } from '@/lib/services/ai-analysis/types';
import { ACTION_ITEM_ASSIGNEE_VALUES } from '@/lib/services/recordings/types';

// ============================================================================
// Enums and Constants
// ============================================================================

// ============================================================================
// Role Types (matching backend MatterRoleDto)
// ============================================================================

/**
 * User summary within a role assignment
 * Matches backend UserSummaryDto
 */
export interface RoleUserSummary {
  id: number;
  /**
   * Nullable (LF-6015). `roles.service.ts` builds this with
   * `plainToInstance(UserSummaryDto, role.lawFirmUser)` — a straight projection
   * of the entity's nullable name columns, with no status filter — and
   * `getAssignableFirmUsers` deliberately keeps `invited` users in the
   * assignment picker, so a nameless user can genuinely hold a matter role.
   * The sibling `MatterRole` / `ActingSolicitor` in `lib/services/tasks/types.ts`
   * already type the same columns this way.
   */
  firstName: string | null;
  lastName: string | null;
  email: string;
}

/**
 * Role assignment on a matter
 * Matches backend MatterRoleDto from DetailedMatterResponseDto
 */
export interface MatterRoleAssignment {
  user: RoleUserSummary;
  role: 'acting_solicitor' | 'assisting_member';
  assignedAt: string;
}

// ============================================================================
// Stage Types (matches backend matter-lifecycle types)
// ============================================================================

export type StageType =
  | 'initial_setup'
  | 'discovery'
  | 'representation'
  | 'collection'
  | 'distribution'
  /*
    **The planning stages** (VALE-1764). A matter carries one set or the other,
    never both: an estate-administration matter has no instructions to record
    and a planning matter has no grant to obtain. They share the `MatterStage`
    shape, the persisted `progress`, and the sidebar's indicator components,
    because the question ("how far has this got, and is there anything for me to
    do") is the same one at both ends of the product.

    The spellings match the planning routes and the sidebar tab ids exactly, so
    one name serves the route, the entry and the measurement.
  */
  | 'planning-instructions'
  | 'planning-drafting'
  | 'planning-execution';

/**
 * The planning stages in workflow order, for the single-dot rule.
 *
 * Deliberately NOT merged into the administration `STAGE_ORDER`. The two sets
 * never coexist on a matter, so one flat array would let an administration
 * stage suppress a planning matter's dot on a stage the matter does not have.
 */
export const PLANNING_STAGE_ORDER: readonly StageType[] = [
  'planning-instructions',
  'planning-drafting',
  'planning-execution'
] as const;

export type StageCompletionState =
  | 'complete'
  | 'in_progress'
  | 'not_started'
  | 'not_applicable';

export interface StageProgress {
  completed: number;
  total: number;
  percent: number;
}

export interface MatterStage {
  stage: StageType;
  completionState: StageCompletionState;
  statusDetail: string | null;
  // Optional for backwards compatibility with matters whose persisted stages
  // predate the progress/attention contract. Use `getStageIndicators(stage)`
  // (lib/services/matters/stage-indicators.ts) to read with safe defaults.
  progress?: StageProgress;
  requiresAttention?: boolean;
}

// ============================================================================
// Checklist Types (for non-estate matters)
// ============================================================================

/**
 * The whole shape of a stored checklist row — **one definition, and the
 * `ChecklistItem` type is derived from it** (VALE-1852).
 *
 * The schema is the declaration rather than a validator bolted beside an
 * interface, because `UpdateMatterRequestSchema` is what the checklist write
 * actually passes through and a zod object **strips every key it does not
 * name**. A second, narrower enumeration of this row in that schema is
 * therefore not a laxer validation of the same shape, it is silent field
 * deletion — and because `PATCH /matters/:id` takes the checklist as a
 * replace-all array, deleting a field there deletes it from **every** row in
 * the matter the first time anybody ticks anything.
 *
 * That is exactly what shipped: the write schema listed `id` / `text` /
 * `isComplete`, so one tick discarded the assignee and the classification off
 * the whole list, permanently, with no error and nothing on screen to say the
 * values had ever been there. Deriving the type from the schema makes the two
 * impossible to disagree: a field added here (VALE-1837 extends this row
 * again) is carried by the write path with no second edit and nothing to
 * remember. `__tests__/checklist-round-trip-registry.test.ts` reconciles the
 * field set against what the write schema actually preserves, so a
 * re-enumeration fails there rather than in production.
 */
export const ChecklistItemSchema = z.object({
  /** UUID */
  id: z.string(),
  /** Item description */
  text: z.string(),
  isComplete: z.boolean(),
  /**
   * Who the row is for, when an AI extraction produced it: the lawyer, the
   * client, or nobody decided yet. Absent on a row a solicitor typed
   * themselves. Bound to the assignee set AI Scribe writes rather than
   * restating its members.
   */
  assignee: z.enum(ACTION_ITEM_ASSIGNEE_VALUES).optional(),
  /**
   * What kind of instruction this row came from, when an AI extraction
   * produced it (VALE-1703). Absent on a row a solicitor typed themselves, and
   * absent on every row imported before the field existed. There is no
   * backfill, by design.
   *
   * Only `hold_delay` renders differently: a standing prohibition has no
   * completion, so it is shown as a hold rather than offered as something to
   * tick. The rest are stored so treating another kind differently later is a
   * rendering decision, not another trip through the import boundary.
   */
  instructionType: z.enum(INSTRUCTION_TYPE_VALUES).optional()
});

export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;

// ============================================================================
// Core Matter Types
// ============================================================================

export interface PostalAddress {
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

/**
 * Why a requested PMS export did not go ahead. Mirrors the backend's
 * `MatterExportSkipReason`; the lawyer-facing wording for each lives in
 * `PMS_EXPORT_SKIP_COPY` (`lib/services/matters/pms-sync.ts`).
 */
export type PmsExportSkipReason =
  | 'MATTER_ARCHIVED'
  | 'NO_REFERENCE_NUMBER'
  | 'MISSING_EXPORT_CONFIGURATION'
  | 'NO_CLIENT_CONTACT'
  | 'CLIENT_NOT_SYNCED';

export interface PmsExportAttempt {
  status: 'success' | 'skipped' | 'failed' | (string & {});
  /** The unmet precondition; null for a failure the PMS itself returned. */
  reasonCode: PmsExportSkipReason | null;
  attemptedAt: string;
}

export interface Matter {
  id: number;
  type?: string; // Matter type slug
  typeLabel?: string; // Matter type display name
  lawFirmInternalReference?: string;
  notes?: string;
  isArchived: boolean;
  decedentTitle?: string;
  decedentFirstName?: string;
  decedentMiddleName?: string;
  decedentLastName?: string;
  decedentDateOfBirth?: string;
  decedentDateOfDeath?: string;
  willDate?: string | null;
  /**
   * Where the original will is held: free text, carried forward from the
   * enquiry at conversion and editable on the matter thereafter (VALE-1402).
   */
  willStorageLocation?: string | null;
  deathCertificateRegistrationNumber?: string | null;
  /** The deceased's occupation, as a court application recites it (VALE-809). */
  decedentOccupation?: string | null;
  /** Where the deceased died, as the death certificate states it — not their last address. */
  decedentPlaceOfDeath?: string | null;
  decedentAddress?: PostalAddress;
  actingSolicitorId?: number;
  actingSolicitor?: {
    id: number;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  isGrantRequired?: boolean;
  hasWill?: boolean;
  hasDeathCertificate?: boolean;
  deathCertificateIsCertifiedCopy?: boolean;
  legalPersonalRepresentatives?: Array<{
    id: number;
    firstName: string;
    middleName?: string;
    lastName: string;
    isLegalPersonalRepresentative?: boolean;
    legalPersonalRepresentativeRole?: string | null;
  }>;
  advertisedAt?: string | null;
  isGrantLodged?: boolean;
  grantLodgedDate?: string;
  lawFirm?: {
    id: number;
    name: string;
  };
  /**
   * The office running this matter, or `null` for firm-wide (VALE-549).
   *
   * `null` is a permanently valid state rather than a gap — every matter that
   * predates its firm's offices carries it, and nothing derives one from the
   * acting solicitor. It decides which of the firm's letterheads the matter's
   * correspondence prints on.
   */
  officeId?: number | null;
  /**
   * The assigned office's name, resolved server-side off the matter's own
   * office row — so an office that has since been **closed** still displays
   * its name here even though {@link Matter.offices} no longer offers it.
   *
   * Detail read only; absent on list rows.
   */
  officeName?: string | null;
  /**
   * The firm's **active** offices, in the firm's own order — the options the
   * Office picker renders. Empty means the firm has no offices, and the Office
   * row is omitted entirely rather than rendering an empty picker.
   *
   * Detail read only; absent on list rows.
   */
  offices?: Array<{ id: number; name: string }>;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  metadata?: {
    clientNames?: string[];
    description?: string;
    pms?: {
      /**
       * The PMS this matter is synced with, set once a sync or import completes.
       *
       * Bound to {@link PmsProvider} rather than restating a union: the hand-typed list here omitted
       * `actionstep` — which the backend has written at runtime since [ASTEP-16] — so every
       * ActionStep-synced matter was mistyped, and the document-import gate could not name the
       * provider without a type error (LF-6344).
       */
      source?: PmsProvider;
      requiresReview?: boolean;
      reviewedAt?: string;
      reviewedBy?: number;
      transferredFromArchivedMatterId?: number;
      matterTypeId?: string;
      matterTypeLabel?: string;
      stateOfMatter?: string;
      representativeOption?: string; // Client role for non-estate-administration matters
    };
  };

  // Stage progress + attention indicators (drives sidebar progress bars + dots)
  stages?: MatterStage[];

  // Related data
  contactsCount?: number;
  inventoryCount?: number;
  tasksCount?: number;
  filesCount?: number;

  // Current user's task for this matter (from backend)
  currentUserTask?: {
    id: number;
    scheduledDate: string;
    position: number;
    description?: string | null;
  };

  roles?: MatterRoleAssignment[];

  estateBankAccount?: {
    accountName: string;
    bsb: string;
    accountNumber: string;
  };
  useTrustAccountForCollection?: boolean;
  tags?: string[];
  checklist?: ChecklistItem[]; // For non-estate matters

  // Grant of Representation fields (QLD/VIC specific)
  grantOfRepresentationCode?: string; // Access code for QLD, Unique identifier code for VIC
  grantOfRepresentationReference?: string; // Application number for VIC only

  // PMS sync status
  pmsSynced?: boolean; // Whether matter has been synced to PMS (Smokeball, etc.)
  pmsProviderName?: PmsProvider | 'leap' | 'clio'; // PMS provider for this matter's law firm
  /**
   * The matter's most recent export attempt to the firm's PMS (VALE-1926). Only on
   * the detail endpoint, only while the matter is not synced; null when no export
   * has been attempted. Read it through `getSettledPmsExportFailure`.
   */
  pmsExportAttempt?: PmsExportAttempt | null;

  /**
   * Whether this matter can send email at all, and from whose mailbox — resolved
   * by the backend using the same decision the send path makes.
   *
   * `null` for every firm not on Outlook transport, which is almost all of them.
   * When `blocked` is true, every send control on this matter must be disabled
   * with an explanation: the send would be refused with a 409.
   *
   * **Never re-derive this by comparing email addresses.** The backend matches
   * the acting solicitor's mailbox by user id; an address comparison diverges on
   * aliases, and in the dangerous direction — blocking a send that would have
   * worked. Read it via `useOutlookMatterSendGate`.
   */
  outlookSendIdentity?: OutlookSendIdentity | null;

  // Clients (for non-estate-administration matters)
  clients?: Array<{
    id: number;
    firstName?: string;
    lastName?: string;
    organisationName?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
  }>;

  // Demo matter flag — when true, every outbound external email is redirected
  // server-side back to the authenticated user. UI surfaces a visual marker
  // and a redirect disclaimer on every send.
  isDemo?: boolean;
  // Backend currently emits this but no UI sets it. The frontend ignores it
  // and always shows the current user's email in the disclaimer.
  demoRecipientEmail?: string | null;
}

export interface GenericContact {
  id: number;
  firstName: string;
  lastName: string;
}

export interface MatterSummary {
  id: number;
  type?: string; // Slug: "estate-administration", etc.
  typeLabel?: string; // Display: "Estate Administration"
  lawFirmInternalReference?: string;
  beneficiaries?: GenericContact[];
  decedentName: string;
  decedentFirstName?: string;
  decedentMiddleName?: string;
  decedentLastName?: string;
  legalPersonalRepresentativeName?: string;
  legalPersonalRepresentative?: string[]; // New backend field - array of LPR names
  hasWill?: boolean;
  dateOfDeath?: string;
  createdAt: string;
  lastUpdated: string;
  assignedTo?: string[];
  tasksOverdue?: number;
  progress?: number;
  isArchived?: boolean;
  legalPersonalRepresentatives?: Array<{
    id: number;
    firstName: string;
    middleName?: string;
    lastName: string;
    isLegalPersonalRepresentative?: boolean;
    legalPersonalRepresentativeRole?: string | null;
  }>;
  stages?: MatterStage[];
  actingSolicitor?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
  } | null;
  roles?: Array<{
    id: number;
    firstName: string | null;
    lastName: string | null;
    role: string;
  }>;
  tags?: string[];
  notes?: string;
  metadata?: {
    clientNames?: string[];
    description?: string;
  };
  // Clients (for non-estate-administration matters)
  clients?: Array<{
    id: number;
    firstName?: string;
    lastName?: string;
    organisationName?: string | null;
  }>;

  isDemo?: boolean;
  demoRecipientEmail?: string | null;

  /**
   * Whether this matter can send email at all, and from whose mailbox — the
   * same field `Matter` carries, now served by the list endpoint too so a
   * lawyer scanning their matters can see which ones cannot send (VALE-347).
   *
   * `null` for every firm not on Outlook transport. Render its
   * `blockedMessage` verbatim; never re-derive the block from an email address,
   * nor the sentence from `blockedReason`.
   */
  outlookSendIdentity?: OutlookSendIdentity | null;

  /**
   * Whether this matter is synced to the firm's CURRENT PMS, and which
   * provider — the same fields `Matter` carries, now served by the list
   * endpoint too so a lawyer scanning their matters can see which are in the
   * PMS without opening each one (VALE-475).
   *
   * `pmsProviderName` is absent for every firm with no enabled PMS. Gate any
   * mark on `pmsSynced` alone — it is already scoped to the firm's *current*
   * integration (see `checkMatterPmsStatus` backend-side), so a matter
   * stranded on a since-retired provider reads `false` here, never `true`.
   */
  pmsSynced?: boolean;
  pmsProviderName?: PmsProvider | 'leap' | 'clio';

  /**
   * Whether this matter has inbound communication nobody at the firm has read
   * yet — the roll-up of the per-thread unread signal the Communications tab
   * and the matter sidebar already show, served by the list endpoint so a
   * lawyer scanning their matters can see which are waiting on the firm
   * without opening each one (VALE-1380).
   *
   * Firm-wide, not per-reader: read marks are recorded against the firm, so a
   * supervisor sees the same flags as the fee earner working the matter. The
   * one per-reader nuance is that a file note never counts as unread for the
   * person who wrote it.
   */
  hasUnreadCommunications?: boolean;
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface CreateMatterRequest {
  type?: MatterTypeSlug;
  lawFirmInternalReference?: string;
  /**
   * Marks the matter as a demo matter. Only accepted when
   * `lawFirmInternalReference` contains `DEMO` — the API returns a 400
   * otherwise. Build it with `resolveDemoMatterFlag`, never from raw checkbox
   * state (`lib/utils/demo-matter.ts`).
   */
  isDemo?: boolean;
  decedentFirstName?: string;
  decedentMiddleName?: string;
  decedentLastName?: string;
  decedentDateOfBirth?: string;
  decedentDateOfDeath?: string;
  decedentAddress?: PostalAddress;
  notes?: string;
  roles?: Array<{
    userId: number;
    role: 'acting_solicitor' | 'assisting_member';
  }>;
  metadata?: {
    clientNames?: string[];
    description?: string;
  };
  /**
   * Reserve the matter rather than create it.
   *
   * The create-matter flow needs a matter id before the user has committed to
   * anything, because the upload and AI-analysis steps hang off one. A reserved
   * matter appears on no list, count or picker, emits no matter-created events,
   * assigns no acting solicitor, and is swept away if it is never committed. It
   * becomes the firm's matter when the flow submits, through the same
   * `PUT /matters/:id` or AI apply-changes it already sends.
   *
   * **Only `useAIDocumentIngestion`'s mount effect sets this**, and it is an
   * explicit statement of intent rather than something the backend infers from
   * an empty body: every other creation path means a real matter.
   * `frontend/docs/create-matter-draft-lifecycle.md` owns the reasoning.
   */
  isDraftReservation?: boolean;
}

export interface UpdateMatterRequest {
  lawFirmInternalReference?: string;
  /**
   * Marks the matter as a demo matter, or clears the mark. `true` is only
   * accepted when the reference the matter ends up with contains `DEMO` — the
   * API validates against the reference in this same request, so renaming a
   * matter into a demo and ticking the box in one save is allowed. `false`
   * always clears cleanly. Build it with `resolveDemoMatterFlag`
   * (`lib/utils/demo-matter.ts`).
   */
  isDemo?: boolean;
  decedentTitle?: string;
  decedentFirstName?: string;
  decedentMiddleName?: string;
  decedentLastName?: string;
  /** Send `YYYY-MM-DD` to set, or `null` to clear. `''` is rejected by the API. */
  decedentDateOfBirth?: string | null;
  /** Send `YYYY-MM-DD` to set, or `null` to clear. `''` is rejected by the API. */
  decedentDateOfDeath?: string | null;
  /** Send `YYYY-MM-DD` to set, or `null` to clear. `''` is rejected by the API. */
  willDate?: string | null;
  /** Where the original will is held: free text. Send `null` to clear. */
  willStorageLocation?: string | null;
  /**
   * The Supreme Court registry the grant application is filed in: one of the grant stage's
   * `jurisdiction.registries` (the API rejects anything else). Send `null` to clear.
   */
  courtRegistry?: string | null;
  /** QLD: the date the Public Trustee was served. Send `YYYY-MM-DD` to set, or `null` to clear. */
  publicTrusteeServedAt?: string | null;
  /** Send a string to set, or `null` to clear an erroneously-populated value. */
  deathCertificateRegistrationNumber?: string | null;
  /** Send a string to set, or `null` to clear. */
  decedentOccupation?: string | null;
  /** Send a string to set, or `null` to clear. */
  decedentPlaceOfDeath?: string | null;
  decedentAddress?: PostalAddress;
  /**
   * The office running this matter — an id from {@link Matter.offices}, or
   * `null` to clear it back to firm-wide (VALE-549). Validated server-side
   * against the acting firm; a closed office already on the matter is still
   * accepted, so editing any other field on it is never refused.
   */
  officeId?: number | null;
  notes?: string;
  isArchived?: boolean;
  isGrantRequired?: boolean;
  isWillRequired?: boolean;
  advertisedAt?: string | null;
  isAdvertismentRequired?: boolean;
  advertisedForDistributionAt?: string | null;
  isAdvertismentForDistributionRequired?: boolean;
  isGrantLodged?: boolean;
  clientIds?: number[]; // Contact IDs to assign as clients (non-estate-administration matters only)
  estateBankAccount?: {
    accountName: string;
    bsb: string;
    accountNumber: string;
  };
  useTrustAccountForCollection?: boolean;
  roles?: Array<{
    userId: number;
    role: 'acting_solicitor' | 'assisting_member';
  }>;
  metadata?: {
    clientNames?: string[];
    description?: string;
    pms?: {
      matterTypeId?: string;
      stateOfMatter?: string;
      representativeOption?: string; // Client role for non-estate-administration matters
    };
  };
  checklist?: ChecklistItem[]; // Full array replacement

  // Grant of Representation fields (QLD/VIC specific)
  grantOfRepresentationCode?: string; // Access code for QLD, Unique identifier code for VIC
  grantOfRepresentationReference?: string; // Application number for VIC only
}

export interface MatterFilters {
  /** @deprecated Use `q` for backend fuzzy search instead */
  search?: string;
  /** Backend fuzzy search query - searches matter reference, deceased name, and LPR names */
  q?: string;
  isArchived?: boolean;
  includeArchived?: 'only' | 'true' | 'false'; // New: 'only' for archived view, 'true' to include all
  assignedToMe?: boolean;
  hasTask?: boolean;
  page?: number;
  limit?: number;
  /** Matter type slugs for filtering (e.g., ['estate-administration', 'estate-planning']) */
  types?: string[];
  /** Team member IDs for filtering */
  teamMemberIds?: number[];
  /** Sort field - matches backend: updatedAt, createdAt, lawFirmInternalReference */
  sortBy?: 'updatedAt' | 'createdAt' | 'lawFirmInternalReference';
  /** Sort direction - matches backend: ASC, DESC */
  sortOrder?: 'ASC' | 'DESC';
  /** Matter tags for filtering (comma-separated when sent to backend) */
  tags?: string[];
  /** When true, only return demo matters (DEMO-001 etc.). */
  isDemo?: boolean;
}

export interface MatterListResponse {
  matters: MatterSummary[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// ============================================================================
// Validation Schemas
// ============================================================================

export const PostalAddressSchema = z.object({
  streetAddress: z.string().optional(),
  suburb: z.string().optional(),
  state: z.string().optional(),
  postcode: z.string().optional(),
  country: z.string().optional()
});

export const CreateMatterRequestSchema = z.object({
  type: z.nativeEnum(MatterTypeSlug).optional(),
  lawFirmInternalReference: z.string().optional(),
  decedentFirstName: z.string().min(1, 'First name is required').optional(),
  decedentMiddleName: z.string().optional(),
  decedentLastName: z.string().min(1, 'Last name is required').optional(),
  decedentDateOfBirth: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date of birth must be YYYY-MM-DD')
    .optional(),
  decedentDateOfDeath: z.string().optional(),
  decedentAddress: PostalAddressSchema.optional(),
  notes: z.string().optional(),
  roles: z
    .array(
      z.object({
        userId: z.number(),
        role: z.enum(['acting_solicitor', 'assisting_member'])
      })
    )
    .optional(),
  /*
    `safeParse` strips anything this schema does not name, so a field the API
    needs is silently dropped unless it is declared here. The create-matter
    flow's reservation flag is the whole difference between an invisible,
    sweepable row and a permanent empty matter in the firm's list, so losing it
    here would look exactly like the fix not working, see
    `CreateMatterRequest.isDraftReservation`.
  */
  isDraftReservation: z.boolean().optional()
});

export const UpdateMatterRequestSchema = z.object({
  lawFirmInternalReference: z.string().optional(),
  decedentTitle: z.string().optional(),
  decedentFirstName: z.string().optional(),
  decedentMiddleName: z.string().optional(),
  decedentLastName: z.string().optional(),
  /*
    Nullable, because clearing a recorded date has to be expressible (VALE-90).
    The backend has always accepted `null` here — `UpdateMatterDto` guards both
    columns with `@Matches(/^\d{4}-\d{2}-\d{2}$/)` alongside `@IsOptional()`,
    and `@IsOptional()` skips its validators for `null` / `undefined` only. An
    empty string is a *value*, so it reaches `@Matches` and 400s; `null` passes
    and nulls the column. Until this said so, no surface could clear a date at
    all: the omission was in the client schema, not in the API.
  */
  decedentDateOfBirth: z.string().nullable().optional(),
  decedentDateOfDeath: z.string().nullable().optional(),
  willDate: z.string().nullable().optional(),
  // 1000, matching `UpdateMatterDto`'s own `@MaxLength(1000)`. The row's
  // `validate` reads this bound rather than restating the number (VALE-1402).
  willStorageLocation: z
    .string()
    .max(1000, 'Must be 1000 characters or fewer')
    .nullable()
    .optional(),
  deathCertificateRegistrationNumber: z
    .string()
    .max(64, 'Must be 64 characters or fewer')
    .nullable()
    .optional(),
  /*
    Nullable for the same reason as the field above: clearing an occupation
    recorded in error is a real edit, and a key missing from this schema is
    stripped from the request without erroring — so the field would appear to
    save and change nothing.
  */
  decedentOccupation: z
    .string()
    .max(255, 'Must be 255 characters or fewer')
    .nullable()
    .optional(),
  decedentPlaceOfDeath: z
    .string()
    .max(255, 'Must be 255 characters or fewer')
    .nullable()
    .optional(),
  decedentAddress: PostalAddressSchema.optional(),
  /*
    Nullable for the same reason the dates above are: clearing a matter's office
    is a real edit meaning *firm-wide*, and the backend's `@IsOptional()` skips
    its `@IsInt()` for `null` (VALE-549). A key missing from this schema is
    stripped from the request without erroring, so the field would appear to
    save and change nothing.
  */
  officeId: z.number().int().positive().nullable().optional(),
  /*
    Marks the matter as a demo matter, or clears the mark (VALE-749). Listed here
    for the same reason `officeId` is: a key missing from this schema is stripped
    from the request without erroring, so the checkbox would appear to save and
    change nothing. The API rejects `true` unless the reference the matter ends up
    with contains `DEMO` — build the value with `resolveDemoMatterFlag`
    (`lib/utils/demo-matter.ts`) rather than sending raw checkbox state.
  */
  isDemo: z.boolean().optional(),
  notes: z.string().optional(),
  archived: z.boolean().optional(), // Deprecated
  isArchived: z.boolean().optional(), // Backend expects this field name
  isGrantRequired: z.boolean().optional(),
  isWillRequired: z.boolean().optional(),
  advertisedAt: z.string().nullable().optional(),
  // The grant-stage matter facts every court form reads (VALE-1988). A key missing here is
  // stripped from the request without erroring, so the write would 200 and change nothing.
  courtRegistry: z.string().nullable().optional(),
  publicTrusteeServedAt: z.string().nullable().optional(),
  isAdvertismentRequired: z.boolean().optional(),
  advertisedForDistributionAt: z.string().nullable().optional(),
  isAdvertismentForDistributionRequired: z.boolean().optional(),
  isGrantLodged: z.boolean().optional(),
  scheduledDate: z.string().optional(),
  estateBankAccount: z
    .object({
      bsb: z
        .string()
        .length(6, 'Must be 6 numbers')
        .regex(/^\d{6}$/, 'Must be numbers only'),
      accountNumber: z
        .string()
        .min(6, 'Must be between 6 and 10 numbers')
        .max(10, 'Must be between 6 and 10 numbers')
        .regex(/^\d{6,10}$/, 'Must be numbers only'),
      accountName: z.string().min(4, 'Must be at least 4 characters')
    })
    .optional(),
  useTrustAccountForCollection: z.boolean().optional(),
  metadata: z
    .object({
      clientNames: z.array(z.string()).optional(),
      description: z.string().optional(),
      pms: z
        .object({
          matterTypeId: z.string().optional(),
          stateOfMatter: z.string().optional(),
          representativeOption: z.string().optional()
        })
        .optional()
    })
    .optional(),
  clientIds: z.array(z.number()).optional(),
  roles: z
    .array(
      z.object({
        userId: z.number(),
        role: z.enum(['acting_solicitor', 'assisting_member'])
      })
    )
    .optional(),
  // Every field a stored row carries, by binding to the row's own shape. NEVER
  // re-enumerate the object here: a zod object strips what it does not name,
  // and this write is replace-all, so a narrower copy silently deletes those
  // fields from the matter's whole checklist (VALE-1852).
  checklist: z.array(ChecklistItemSchema).optional(),
  grantOfRepresentationCode: z.string().optional(),
  grantOfRepresentationReference: z.string().optional()
});

// ============================================================================
// Action Result Types
// ============================================================================

export interface MatterActionResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  validationErrors?: Record<string, string[]>;
}

/**
 * `GET /matters/:id/deletion-eligibility` (VALE-1927). The server owns the rule and enforces it
 * on delete; this only lets the app show the reasons, and archive, instead of a confirmation.
 */
export interface MatterDeletionBlocker {
  code: string;
  label: string;
}

export interface MatterDeletionEligibility {
  deletable: boolean;
  blockers: MatterDeletionBlocker[];
  /** The PMS the matter is linked to, left untouched by the delete. */
  pmsProvider: string | null;
}
